import { LeagueClassicPage } from './LeagueClassicPage.js';
import { LeagueMediaMini }   from './LeagueMediaMini.js';
/**
 * LeagueBPI.js &#8212; Liga BPI (Campeonato Nacional Feminino) &#8212; Portugal
 * Architecture mirrors LeagueNWSL.js &#8212; responsive, CSS-injected.
 */
import {
  BPI_STANDINGS_FALLBACK,
  BPI_TOP_SCORERS_FALLBACK,
  BPI_FIXTURES_FALLBACK,
  BPI_SEASON,
  fetchLiveBPIData,
} from '../../data/bpiData.js';
import { injectCSS, gdHtml, formPills, fmtDate, BASE_CSS } from '../../leagueBase.js';

const STYLE_ID = 'leaguebpi-styles';
const ACCENT   = '#CC0000';

function inject() {
  injectCSS(STYLE_ID, BASE_CSS + `
    .leaguebpi-tab.active { border-bottom-color: ${ACCENT}; }
    .leaguebpi-badge      { background: ${ACCENT}; color:#fff; }
    .leaguebpi-scorer-g   { color: ${ACCENT}; }
    .leaguebpi-pts        { color: ${ACCENT}; font-weight:700; }
  `);
}

function fixtureSidebar(fixtures = []) {
  const shown = [
    ...fixtures.filter(f => f.status === 'FT').slice(-3),
    ...fixtures.filter(f => f.status === 'NS').slice(0, 3),
  ];
  return shown.map(f => `
    <div class="lg-fix">
      <div class="lg-fix-date">${fmtDate(f.date)}</div>
      <div class="lg-fix-teams">
        <div class="lg-fix-team"><div class="lg-dot" style="background:${f.homeColor||ACCENT}"></div><span class="lg-fix-name">${f.home}</span></div>
        <div class="lg-fix-team"><div class="lg-dot" style="background:${f.awayColor||'#999'}"></div><span class="lg-fix-name">${f.away}</span></div>
      </div>
      <div class="lg-fix-score ${f.status==='NS'?'upcoming':''}">${f.status==='NS'?'vs':`${f.homeGoals}&#8211;${f.awayGoals}`}</div>
    </div>`).join('');
}

function scorerSidebar(scorers = []) {
  return scorers.slice(0, 6).map((s, i) => `
    <div class="lg-scorer">
      <div class="lg-scorer-rank">${i+1}</div>
      <div class="lg-av">${s.initials}</div>
      <div class="lg-scorer-info">
        <div class="lg-scorer-name">${s.name}</div>
        <div class="lg-scorer-club">${s.club}</div>
      </div>
      <div class="lg-scorer-g">${s.goals}</div>
    </div>`).join('');
}

function standingsRows(rows = []) {
  return rows.map(t => `
    <tr class="lg-zone-${t.zone||'n'}">
      <td>${t.pos}</td>
      <td class="l"><div class="lg-team-cell"><div class="lg-dot" style="background:${t.color}"></div><span class="lg-name">${t.name}</span></div></td>
      <td>${t.p}</td>
      <td class="lg-col-hide">${t.w}</td>
      <td class="lg-col-hide">${t.d}</td>
      <td class="lg-col-hide">${t.l}</td>
      <td class="lg-col-hide">${gdHtml(t.gd)}</td>
      <td class="lg-pts">${t.pts}</td>
      <td class="lg-col-hide"><div class="lg-form">${formPills(t.form||[])}</div></td>
    </tr>`).join('');
}

export class LeagueBPI {
  constructor(containerId) {
    this.containerId = containerId;
    this.root        = null;
    this.data = {
      standings: BPI_STANDINGS_FALLBACK,
      scorers:   BPI_TOP_SCORERS_FALLBACK,
      fixtures:  BPI_FIXTURES_FALLBACK,
    };
  }

  async init() {
    inject();
    this.root = document.getElementById(this.containerId);
    if (!this.root) return;
    const live = await fetchLiveBPIData();
    if (live) {
      if (live.standings)  this.data.standings = live.standings;
      if (live.topScorers) this.data.scorers   = live.topScorers;
      if (live.fixtures)   this.data.fixtures  = live.fixtures;
    }
    this.render();
  }

  render() {
    this.root.innerHTML = this._html();
    this._attachListeners();
  }

  _html() {
    const { standings, scorers, fixtures } = this.data;
    return `
    <div class="lg-wrap">
      <div class="lg-hero">
        <div class="lg-logo-box" style="background:transparent;"><img src="/images/league_bpi.png" style="width:40px;height:40px;object-fit:contain;display:block;"></div>
        <div class="lg-hero-text">
          <div class="lg-hero-title">Liga BPI &#8212; Portugal</div>
          <div class="lg-hero-sub">${BPI_SEASON} &#183; 10 Clubs &#183; Benfica defending champions</div>
        </div>
      </div>

      <div class="lg-tabs" role="tablist">
        <button class="lg-tab leaguebpi-tab active" data-tab="standings">Table</button>
        <button class="lg-tab leaguebpi-tab" data-tab="scorers">Scorers</button>
        <button class="lg-tab leaguebpi-tab" data-tab="clubs">Clubs</button>
        <button class="lg-tab leaguebpi-tab" data-tab="media">Media</button>
        <button class="lg-tab leaguebpi-tab" data-tab="classic">Classic</button>
        <button class="lg-tab leaguebpi-tab" data-tab="about">About</button>
      </div>

      <div class="lg-body">
        <div class="lg-main">
          <div data-panel="standings">
            <table class="lg-table">
              <thead><tr>
                <th>#</th><th class="l">Club</th><th>P</th>
                <th class="lg-col-hide">W</th><th class="lg-col-hide">D</th><th class="lg-col-hide">L</th>
                <th class="lg-col-hide">GD</th><th>Pts</th><th class="lg-col-hide">Form</th>
              </tr></thead>
              <tbody>${standingsRows(standings)}</tbody>
            </table>
            <div class="lg-legend">
              <span class="lg-legend-dot champion"></span>Champions
              &nbsp;&#183;&nbsp;<span class="lg-legend-dot rel"></span>Relegation
            </div>
          </div>
          <div data-panel="scorers" style="display:none">
            <div class="lg-scorer-list">${scorerSidebar(scorers)}</div>
          </div>
          <div data-panel="fixtures" style="display:none">
            <div class="lg-fix-list">${fixtureSidebar(fixtures)}</div>
          </div>
          <div data-panel="clubs" style="display:none"><div style="padding:12px 0;"><div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(130px,1fr));gap:10px;">${(standings||[]).map(t=>`<div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:10px;padding:14px 10px;text-align:center;"><div style="width:44px;height:44px;border-radius:50%;background:${t.color||'#888'};display:flex;align-items:center;justify-content:center;margin:0 auto 8px;font-size:12px;font-weight:700;color:#fff;">${t.short||'?'}</div><div style="font-size:11px;font-weight:600;color:var(--text-color,#111);line-height:1.3;margin-bottom:4px;">${t.name||''}</div><div style="font-size:10px;color:var(--text-secondary,#888);">#${t.pos||''} &middot; ${t.pts||0} pts</div></div>`).join('')}</div></div></div>
          <div data-panel="media" style="display:none"><div style="padding:40px 20px;text-align:center;color:var(--text-secondary,#888);"><div style="font-size:15px;font-weight:600;">Media - Coming Soon</div></div></div>
          <div data-panel="classic" style="display:none">
            <div class="wsl-panel" id="bpi-panel-media"></div>
            <div class="wsl-panel" id="bpi-panel-classic"></div><div style="padding:40px 20px;text-align:center;color:var(--text-secondary,#888);"><div style="font-size:15px;font-weight:600;">Classic - Coming Soon</div></div></div>

          <div data-panel="about" style="display:none"><div style="padding:14px;max-width:900px;"><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#C8102E;">&#9632; Overview</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;">Portugal&#39;s top women&#39;s football league, the Liga BPI. A growing competition as Portugal invests in women&#39;s football following the success of their men&#39;s national team model.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#C8102E;">&#9632; History</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;">Sporting CP and Benfica have dominated Portuguese women&#39;s football, mirroring the men&#39;s game&#39;s power structure. The league has attracted greater investment since the mid-2010s.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#C8102E;">&#9632; Honours</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;"><strong>Sporting CP</strong> and <strong>Benfica</strong> have won the majority of Liga BPI titles. SC Braga have also emerged as strong challengers in recent seasons.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#C8102E;">&#9632; Stars</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;">Portuguese women&#39;s football has produced Dolores Silva and players across Europe. Several top Liga BPI performers have earned moves to WSL and French league clubs.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#C8102E;">&#9632; TV &amp; Media</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;">Broadcast on <strong>Sport TV</strong> in Portugal. The federation has increased coverage as part of a broader push to grow women&#39;s football visibility.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#C8102E;">&#9632; Europe</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;">Sporting and Benfica compete in UWCL qualifying. Portugal&#39;s women&#39;s team has qualified for European Championships, raising the league&#39;s profile.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#C8102E;">&#9632; Key Facts</h4><div><span style="display:inline-block;background:var(--hover-bg,#f5f5f5);border-radius:6px;padding:3px 8px;margin:2px;font-size:11px;font-weight:600;">10 clubs</span><span style="display:inline-block;background:var(--hover-bg,#f5f5f5);border-radius:6px;padding:3px 8px;margin:2px;font-size:11px;font-weight:600;">FPF governed</span><span style="display:inline-block;background:var(--hover-bg,#f5f5f5);border-radius:6px;padding:3px 8px;margin:2px;font-size:11px;font-weight:600;">Sep&#8211;May</span><span style="display:inline-block;background:var(--hover-bg,#f5f5f5);border-radius:6px;padding:3px 8px;margin:2px;font-size:11px;font-weight:600;">Growing fast</span></div></div></div></div>
        </div>
      </div>
    </div>`;
  }

  _attachListeners() {
    this.root.querySelectorAll('.lg-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        this.root.querySelectorAll('.lg-tab').forEach(b => b.classList.remove('active'));
        btn.classList.add('active')
        const _tt=btn.dataset.tab||'';if(_tt==='classic'){const _ce=this.root&&this.root.querySelector('#bpi-panel-classic');if(_ce&&!_ce._cpm){new LeagueClassicPage(_ce,'bpi').init();_ce._cpm=true;}}if(_tt==='media'){const _me=this.root&&this.root.querySelector('#bpi-panel-media');if(_me&&!_me._mmm){new LeagueMediaMini(_me,'bpi').init();_me._mmm=true;}};
        const tab = btn.dataset.tab;
        this.root.querySelectorAll('[data-panel]').forEach(p => {
          p.style.display = p.dataset.panel === tab ? '' : 'none';
        });
      });
    });
  }
}
